import { map, Observable } from "rxjs";
import HttpClient from "./HttpClient";
import { APIHosts, ApiReduxHelpers } from "./reduxHelpers";
import projectImage from "@/assets/projectImage.svg";

// --- backend DTOs (align with ICDaaS.Web.Shared.Models) ---
export type ProjectSummaryDto = {
  projectId: number;
  projectRefId: number | null;
  facilityId: number;
  title: string;
  alias: string;
  created: string;
  updated: string;
  projectStatus: string;
  projectSchema: string;
  status: boolean;
  timezone: string;
  procoreProjectId: number;
  projectInspectionNumber: number;
  projectMarkupsandnotesNumber: number;
  code: string;
  masterformat: string;
  inspectionSpecSectionType: number;
};

export interface ProjectResponse {
  id: number;
  code: string;
  parentId?: number;
  projectGuid: string;
  companyId: number;
  name: string;
  startDate: string;
  endDate: string;
  address: string;
  description: string;
  avatar: string;
  managerName: string;
  managerId: number;
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  status: number;
  maKho: string[];
}

export type ActiveModuleDto = {
  moduleId: number;
  name: string;
  isActive: boolean;
};

export type WorkflowDto = {
  projectId: number;
  steps: { id: number; name: string; status: string }[];
};

export type UpdateProjectRequest = {
  // whatever your API expects
  name?: string;
  description?: string;
};

export type UpdateProjectResponse = {
  projectId: number;
  message: string;
};

export type ChangeProjectResponse = { projectId: number; message: string };
export type CompanyRoleResponse = { projectId: number; role: string };

// --- Filter API types ---
export type FilterProjectsRequest = {
  page?: number;
  pageSize?: number;
  search?: string;
  ownership?: "owned" | "collaborated";
  status?: "active" | "archived";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

export type PagedProjectResponse = {
  projects: ProjectSummaryDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

// --- UI model used across the app ---
export type Project = {
  id: number;
  name: string;
  account: string;
  image: string;
  status: string;
  integrations: string[];
  lastModified: string;
  statusColor: string;
};

// map backend -> UI Project
function statusColor(status?: string): string {
  const s = (status ?? "").toLowerCase();
  if (s.includes("disconnect")) return "#ff4d4f";
  if (s.includes("update")) return "#faad14";
  return "#52c41a";
}
function pick<T>(...vals: (T | undefined)[]): T | undefined {
  return vals.find((v) => v !== undefined);
}
export function mapToProject(dto: ProjectSummaryDto): Project {
  const id = dto.projectId;
  const name = dto.title;
  const account = dto.alias;
  const image = projectImage;
  const statusTxt = dto.projectStatus
    ? dto.projectStatus.charAt(0).toUpperCase() + dto.projectStatus.slice(1)
    : "Unknown";
  const lastModified = new Date(dto.updated).toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  // Thêm integrations dựa trên project data
  const integrations: string[] = [];
  if (dto.procoreProjectId > 0) {
    integrations.push("Procore");
  }
  // Luôn có Autodesk cho demo
  integrations.push("Autodesk");
  return {
    id,
    name,
    account,
    image,
    status: statusTxt,
    integrations,
    lastModified,
    statusColor: statusColor(statusTxt),
  };
}

export const mapProjectResponseToProject = (p: ProjectResponse): Project => {
  return {
    id: p.id,
    name: p.name,
    account: p.managerName || p.ownerName || "",
    image: projectImage,
    status: "Updated",
    integrations: ["Procore", "Autodesk"],
    lastModified: p.endDate || p.startDate || "",
    statusColor: "#52c41a",
  };
};

// --- service ---
class ProjectsService extends ApiReduxHelpers {
  constructor() {
    super();
  }

  // POINT THIS to the host that serves your C# Web.Server /api routes.
  // If you already have a constant like APIHosts.App or APIHosts.Web, use that.
  ApiHost = APIHosts.Project;

  private envHeaders() {
    const token = localStorage.getItem("accessToken") || "";
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  getProjects(params?: { page?: number; pageSize?: number }) {
    const page = params?.page ?? 1;
    const pageSize = params?.pageSize ?? 30;
    return HttpClient.get(
      `${this.getHost(
        this.ApiHost
      )}/api/projects?page=${page}&pageSize=${pageSize}`,
      { headers: this.envHeaders() }
    );
  }

  getActiveModules(projectId: number): Observable<ActiveModuleDto[]> {
    return HttpClient.get(
      `${this.getHost(this.ApiHost)}/api/projects/${projectId}/modules`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  getWorkflow(projectId: number): Observable<WorkflowDto> {
    return HttpClient.get(
      `${this.getHost(this.ApiHost)}/api/projects/${projectId}/workflow`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  updateProject(
    projectId: number,
    body: UpdateProjectRequest
  ): Observable<UpdateProjectResponse> {
    return HttpClient.put(
      `${this.getHost(this.ApiHost)}/api/projects/${projectId}`,
      body,
      {
        headers: this.envHeaders(),
      }
    );
  }

  changeProject(projectId: number): Observable<ChangeProjectResponse> {
    return HttpClient.get(
      `${this.getHost(this.ApiHost)}/api/projects/${projectId}/change`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  getCompanyRole(projectId: number): Observable<CompanyRoleResponse> {
    return HttpClient.get(
      `${this.getHost(this.ApiHost)}/api/projects/${projectId}/company-role`,
      {
        headers: this.envHeaders(),
      }
    );
  }

  filterProjects(
    params: FilterProjectsRequest
  ): Observable<PagedProjectResponse> {
    // Build query parameters
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append("page", params.page.toString());
    if (params.pageSize)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.ownership) queryParams.append("ownership", params.ownership);
    if (params.status) queryParams.append("status", params.status);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const queryString = queryParams.toString();
    const url = `${this.getHost(this.ApiHost)}/api/projects/filter${
      queryString ? `?${queryString}` : ""
    }`;

    return HttpClient.get(url, {
      headers: this.envHeaders(),
    });
  }
  getProjectsByCompanyId(companyId: number): Observable<ProjectResponse[]> {
    const url = `${this.getHost(
      this.ApiHost
    )}/api/Project/company/${companyId}`;
    return HttpClient.get(url).pipe(map((res) => res as ProjectResponse[]));
  }
  
  
  
  
 
}

const projectsService = new ProjectsService();
export default projectsService;
